import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { Video, Target, Code, FileText, HelpCircle, ClipboardList } from 'lucide-react';

const CourseCreationContext = createContext();

export const useCourseCreation = () => {
  const context = useContext(CourseCreationContext);
  if (!context) {
    throw new Error('useCourseCreation must be used within a CourseCreationProvider');
  }
  return context;
};

export const CourseCreationProvider = ({ children }) => {
  // Drafts locaux (état éditeur en mémoire)
  const [courseDrafts, setCourseDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  // Types de contenu supportés
  const contentTypes = [
    {
      id: 'video',
      name: 'Vidéo',
      description: 'Ajoutez une vidéo avec transcription',
      Icon: Video,
      fields: ['title', 'videoUrl'],
    },
    {
      id: 'interactive',
      name: 'Présentation Interactive',
      description: 'Créez des slides avec quiz et code',
      Icon: Target,
      fields: ['title', 'slides'],
    },
    {
      id: 'exercise',
      name: 'Exercice Pratique',
      description: 'Exercices de code avec tests',
      Icon: Code,
      fields: ['title', 'instructions'],
    },
    {
      id: 'document',
      name: 'Document',
      description: 'Contenu textuel avec fichiers joints',
      Icon: FileText,
      fields: ['title', 'content'],
    },
    {
      id: 'quiz',
      name: 'Quiz',
      description: 'Questionnaire à choix multiples',
      Icon: HelpCircle,
      fields: ['title', 'questions'],
    },
    {
      id: 'assignment',
      name: 'Devoir',
      description: 'Travail à rendre avec évaluation',
      Icon: ClipboardList,
      fields: ['title', 'instructions'],
    },
  ];

  const contentTemplates = {
    video: {
      title: '',
      description: '',
      videoUrl: '',
      transcript: '',
      duration: '00:00',
    },
    interactive: {
      title: '',
      description: '',
      slides: [{ id: 1, title: 'Slide 1', content: '', image: '', code: '' }],
      quiz: { questions: [{ id: 1, question: '', options: ['', '', '', ''], correct: 0, explanation: '' }] },
      sandbox: { starterCode: '// Votre code ici\n', dependencies: [] },
    },
    exercise: {
      title: '',
      description: '',
      instructions: '',
      starterCode: 'function solution() {\n  // Votre code ici\n  return null;\n}',
      testCases: [{ input: '', expected: '', description: '' }],
    },
    document: {
      title: '',
      description: '',
      content: '<h2>Titre</h2>\n<p>Contenu...</p>',
      attachments: [],
    },
    quiz: {
      title: '',
      description: '',
      questions: [{ id: 1, question: '', type: 'multiple-choice', options: ['', '', '', ''], correct: 0, explanation: '' }],
      timeLimit: 30,
    },
    assignment: {
      title: '',
      description: '',
      instructions: '',
      rubric: [{ criterion: '', points: 10, description: '' }],
      dueDate: '',
      maxPoints: 100,
    },
  };

  // ── Gestion locale des drafts (compatible avec ContentEditor) ──────────────

  /**
   * Crée un draft local à partir du template — pas d'appel API ici.
   * Le draft aura `lessonId: null` jusqu'au premier saveContentDraft().
   */
  const createContentDraft = (courseId, moduleId, contentType) => {
    const draftId = `draft-${courseId}-${moduleId}-${Date.now()}`;
    const template = contentTemplates[contentType] || {};
    const draft = {
      id: draftId,
      courseId,
      moduleId,
      type: contentType,
      lessonId: null, // sera rempli après le premier save API
      ...template,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setCourseDrafts(prev => ({ ...prev, [draftId]: draft }));
    return draft;
  };

  /** Met à jour le draft local (appelé à chaque modification dans l'éditeur) */
  const updateContentDraft = (draftId, updates) => {
    setCourseDrafts(prev => ({
      ...prev,
      [draftId]: {
        ...prev[draftId],
        ...updates,
        updatedAt: new Date().toISOString(),
      },
    }));
  };

  /** Supprime le draft local (et la leçon API si elle a été créée) */
  const deleteContentDraft = async (draftId) => {
    const draft = courseDrafts[draftId];
    if (draft?.lessonId) {
      try { await axios.delete(`/api/lessons/${draft.lessonId}`); } catch (_) {}
    }
    setCourseDrafts(prev => {
      const next = { ...prev };
      delete next[draftId];
      return next;
    });
  };

  // ── Extraction du contenu selon le type ───────────────────────────────────

  const extractContent = (draft) => {
    switch (draft.type) {
      case 'video':
        return { videoUrl: draft.videoUrl, transcript: draft.transcript, duration: draft.duration };
      case 'document':
        return { text: draft.content, attachments: draft.attachments };
      case 'quiz':
        return { questions: draft.questions, timeLimit: draft.timeLimit };
      case 'exercise':
        return { instructions: draft.instructions, starterCode: draft.starterCode, testCases: draft.testCases };
      case 'interactive':
        return { slides: draft.slides, quiz: draft.quiz, sandbox: draft.sandbox };
      case 'assignment':
        return { instructions: draft.instructions, rubric: draft.rubric, dueDate: draft.dueDate, maxPoints: draft.maxPoints };
      default:
        return {};
    }
  };

  // ── Sauvegarde / publication via API ──────────────────────────────────────

  /**
   * saveContentDraft(draftId) — signature compatible avec ContentEditor.
   * - Si pas encore de lessonId → crée la leçon via POST /api/lessons
   * - Sinon → met à jour via PUT /api/lessons/:id (brouillon)
   */
  const saveContentDraft = async (draftId, updates = null) => {
    const base = courseDrafts[draftId];
    if (!base) return { success: false, error: 'Draft introuvable' };
    const draft = updates ? { ...base, ...updates } : base;

    setSaving(true);
    try {
      if (draft.lessonId) {
        const { data } = await axios.put(`/api/lessons/${draft.lessonId}`, {
          title:       draft.title || 'Sans titre',
          content:     extractContent(draft),
          isPublished: false,
        });
        setCourseDrafts(prev => ({ ...prev, [draftId]: { ...prev[draftId], status: 'saved' } }));
        return { success: true, lesson: data.lesson };
      } else {
        const { data } = await axios.post('/api/lessons', {
          moduleId:          draft.moduleId,
          courseId:          draft.courseId,
          title:             draft.title || 'Sans titre',
          type:              draft.type,
          order:             0,
          content:           extractContent(draft),
          isPublished:       false,
          estimatedDuration: 0,
        });
        setCourseDrafts(prev => ({
          ...prev,
          [draftId]: { ...prev[draftId], lessonId: data.lesson.id, status: 'saved' },
        }));
        return { success: true, lesson: data.lesson };
      }
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  /**
   * publishContent(draftId) — signature compatible avec ContentEditor.
   * Crée la leçon si nécessaire, puis la publie (isPublished: true).
   */
  const publishContent = async (draftId, updates = null) => {
    const base = courseDrafts[draftId];
    if (!base) return { success: false, error: 'Draft introuvable' };
    const draft = updates ? { ...base, ...updates } : base;

    setSaving(true);
    try {
      let lessonId = draft.lessonId;

      if (!lessonId) {
        // Créer + publier en une passe
        const { data } = await axios.post('/api/lessons', {
          moduleId:          draft.moduleId,
          courseId:          draft.courseId,
          title:             draft.title || 'Sans titre',
          type:              draft.type,
          order:             0,
          content:           extractContent(draft),
          isPublished:       true,
          estimatedDuration: 0,
        });
        lessonId = data.lesson.id;
        setCourseDrafts(prev => ({
          ...prev,
          [draftId]: { ...prev[draftId], lessonId, status: 'published' },
        }));
        return { success: true, lesson: data.lesson };
      } else {
        const { data } = await axios.put(`/api/lessons/${lessonId}`, {
          title:       draft.title || 'Sans titre',
          content:     extractContent(draft),
          isPublished: true,
        });
        setCourseDrafts(prev => ({ ...prev, [draftId]: { ...prev[draftId], status: 'published' } }));
        return { success: true, lesson: data.lesson };
      }
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  // ── API directe modules/leçons (pour d'autres composants) ─────────────────

  const createModule = async ({ courseId, title, description = '', order = 0, estimatedDuration = 0 }) => {
    setSaving(true);
    try {
      const { data } = await axios.post('/api/modules', { courseId, title, description, order, estimatedDuration });
      return { success: true, module: data.module };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  const updateModule = async (moduleId, updates) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`/api/modules/${moduleId}`, updates);
      return { success: true, module: data.module };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  const deleteModule = async (moduleId) => {
    setSaving(true);
    try {
      await axios.delete(`/api/modules/${moduleId}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  const createLesson = async ({ moduleId, courseId, title, type, order = 0, estimatedDuration = 0, isPreview = false, content = {} }) => {
    setSaving(true);
    try {
      const { data } = await axios.post('/api/lessons', { moduleId, courseId, title, type, order, estimatedDuration, isPreview, content });
      return { success: true, lesson: data.lesson };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async (lessonId) => {
    setSaving(true);
    try {
      await axios.delete(`/api/lessons/${lessonId}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setSaving(false);
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const getContentType   = (typeId) => contentTypes.find(t => t.id === typeId);
  const getContentDrafts = (courseId, moduleId) =>
    Object.values(courseDrafts).filter(d => d.courseId === courseId && d.moduleId === moduleId);

  const validateContent = (content, type) => {
    const typeConfig = getContentType(type);
    if (!typeConfig) return { valid: false, errors: ['Type de contenu invalide'] };
    const errors = [];
    typeConfig.fields.forEach(field => {
      const val = content[field];
      const empty = !val || (typeof val === 'string' && !val.trim()) || (Array.isArray(val) && !val.length);
      if (empty) errors.push(`Le champ "${field}" est requis`);
    });
    if (type === 'video' && content.videoUrl && !/^https?:\/\/(www\.)?(youtube|youtu\.be|vimeo)|\.(mp4|webm|ogg)$/i.test(content.videoUrl)) {
      errors.push('URL vidéo invalide (YouTube, Vimeo ou .mp4/.webm)');
    }
    if (type === 'quiz' && content.questions?.length === 0) errors.push('Au moins une question est requise');
    return { valid: errors.length === 0, errors };
  };

  const duplicateContent = (draftId) => {
    const original = courseDrafts[draftId];
    if (!original) return null;
    const newId = `draft-${original.courseId}-${original.moduleId}-${Date.now()}`;
    const duplicate = { ...original, id: newId, title: `${original.title} (Copie)`, lessonId: null, status: 'draft', createdAt: new Date().toISOString() };
    setCourseDrafts(prev => ({ ...prev, [newId]: duplicate }));
    return duplicate;
  };

  const value = {
    courseDrafts,
    saving,
    loading: saving, // alias pour compatibilité
    contentTypes,
    contentTemplates,
    // Draft local
    createContentDraft,
    updateContentDraft,
    deleteContentDraft,
    getContentDrafts,
    duplicateContent,
    // API
    saveContentDraft,
    publishContent,
    createModule,
    updateModule,
    deleteModule,
    createLesson,
    deleteLesson,
    // Utilitaires
    getContentType,
    validateContent,
  };

  return (
    <CourseCreationContext.Provider value={value}>
      {children}
    </CourseCreationContext.Provider>
  );
};
